import { useEffect, useState } from "react";
import { TAG_ICONS, cleanTagList, editsCatalog, kindOf, tagKey } from "@speisekarte/core";
import type { DishKind } from "@speisekarte/core";
import { KindSwitch, drinksInPlay } from "./Dishes.tsx";
import { TagSymbol } from "../icon.tsx";
import { GRIP_PATH, useSortable } from "../sortable.ts";
import { useDraft, useKept } from "../kept.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import { PhraseLine } from "./TextWork.tsx";

interface Row {
  /** Stable key while editing. */
  key: number;
  name: string;
  /** The name it has on the server; undefined for a new one. */
  from?: string;
  /** Its symbol (S21); empty: none. */
  icon: string;
}

const rowsOf = (list: string[], icons: Record<string, string>): Row[] => list.map((name, key) => ({ key, name, from: name, icon: icons[name] ?? "" }));
let nextKey = 10_000;

/**
 * „Tags“: the business's list (S17), saved as a whole, as the categories, each change as one leaves the
 * field or picks a symbol (no „Speichern“, Martin, 01.11.2026). Renaming renames at every
 * dish and on every menu; removing takes the tag off the dishes (the number on the right says how
 * many have it). The order, by the grip, is the order in the form and on „Auf der Karte“. Drinks
 * (S22) have a list of their own, behind a switch that shows only when the business has drinks.
 */
export function Tags({ kind = "food" }: { kind?: DishKind }) {
  const s = useStore();
  const data = s.data!;
  const canEdit = editsCatalog(s.role);
  const drink = kind === "drink";
  const list = drink ? data.drinkTags : data.tags;

  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  const saved = JSON.stringify([list, data.tagIcons]);
  // The list as being changed, kept while one looks elsewhere (S17); anew when it was saved.
  const [rows, setRows] = useDraft<Row[]>(drink ? "tags:drink" : "tags", saved, () => rowsOf(list, data.tagIcons));
  // New ones apart until they have a name: saved, they join the list at its end.
  const [fresh, setFresh] = useKept<Row[]>(drink ? "tags-new:drink" : "tags-new", []);
  useEffect(() => setState({}), [kind]);
  /** The row whose symbol is being chosen. */
  const [picking, setPicking] = useState<number | null>(null);
  const used = new Map<string, number>();
  for (const d of data.dishes) if (kindOf(d) === kind) for (const t of d.tags) used.set(tagKey(t), (used.get(tagKey(t)) ?? 0) + 1);
  const sort = useSortable(
    (from, to) => {
      const next = [...rows];
      const [row] = next.splice(from.index, 1);
      next.splice(to.index, 0, row!);
      void save(next);
    },
    (from, by) => {
      const at = from.index + by;
      if (at < 0 || at >= rows.length) return;
      const next = [...rows];
      [next[from.index], next[at]] = [next[at]!, next[from.index]!];
      void save(next);
    },
  );

  /** The list with the new ones that have a name; nothing when it is as saved. */
  async function save(next: Row[], pending = fresh) {
    const all = [...next, ...pending.filter((r) => r.name.trim())];
    if (JSON.stringify(all.map((r) => [r.name, r.from, r.icon])) === JSON.stringify(list.map((c) => [c, c, data.tagIcons[c] ?? ""]))) return setRows(next);
    const entries = all.map((r) => ({ name: r.name, ...(r.from && r.from !== r.name ? { from: r.from } : {}), ...(r.icon ? { icon: r.icon } : {}) }));
    const clean = cleanTagList(entries);
    if ("error" in clean) return [setRows(next), setState({ error: clean.error })];
    const gone = list.filter((t) => !next.some((r) => r.from === t));
    const hit = gone.reduce((n, t) => n + (used.get(tagKey(t)) ?? 0), 0);
    const losing = drink ? (hit === 1 ? "Ein Getränk verliert" : `${hit}-mal verlieren Getränke`) : hit === 1 ? "Eine Speise verliert" : `${hit}-mal verlieren Speisen`;
    if (hit && !confirm(`${gone.map((t) => `„${t}“`).join(", ")} entfernen? ${losing} den Tag.`)) return;
    setRows(next);
    try {
      await store.change(() => api(`/api/tags${drink ? "?kind=drink" : ""}`, { method: "PUT", body: { tags: clean.value } }));
      setFresh(pending.filter((r) => !r.name.trim()));
      setState({ ok: "Gespeichert" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  return (
    <div>
      <p>
        <a className="back" href={drink ? "#/getraenke" : "#/kataloge"}>{drink ? "← Getränke" : "← Kataloge"}</a>
      </p>
      <section className="card">
        <h2>Tags</h2>
        {drinksInPlay(data) && <KindSwitch kind={kind} food="#/tags" drink="#/tags?art=getraenke" />}
        <p className="hint">
          {drink
            ? "Merkmale der Getränke wie alkoholfrei, vom Fass, bio: zum Suchen und Filtern, und auf der Speisekarte, wo sie gewählt sind. „alkoholfrei“ zählt auch für den Hinweis zum Jugendschutz."
            : "Merkmale der Speisen wie vegetarisch, scharf, hausgemacht: zum Suchen und Filtern, und auf der Speisekarte, wo sie gewählt sind. So geschrieben, wie sie hier stehen."}
        </p>
        <ul className="list category-list" data-sort-root>
          <li data-sort-group="list" style={{ border: "none" }} className={sort.groupClass("list", rows.length)}>
            {rows.map((r, i) => {
              const n = r.from ? (used.get(tagKey(r.from)) ?? 0) : 0;
              return (
                <div key={r.key} className={`category-row tag-row ${sort.rowClass({ group: "list", index: i })}`} data-sort-row data-testid="tag-row">
                  <button className={`symbol-pick ${r.icon ? "on" : ""}`} onClick={() => setPicking(picking === r.key ? null : r.key)} disabled={!canEdit} aria-label={r.icon ? `Symbol von ${r.name} ändern` : `Symbol für ${r.name} wählen`} title={r.icon ? "Symbol ändern" : "Symbol wählen"} data-testid="tag-symbol">
                    {r.icon ? <TagSymbol id={r.icon} /> : <span aria-hidden="true">◇</span>}
                  </button>
                  <input value={r.name} onChange={(e) => [setRows(rows.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x))), setState({})]} onBlur={() => void save(rows)} readOnly={!canEdit} aria-label="Name des Tags" placeholder={drink ? "z. B. vom Fass" : "z. B. hausgemacht"} />
                  <span className="muted small count" title={drink ? "So viele Getränke haben diesen Tag" : "So viele Speisen haben diesen Tag"}>
                    {n || ""}
                  </span>
                  {canEdit && (
                    <>
                      <button className="link danger" onClick={() => void save(rows.filter((x) => x.key !== r.key))} aria-label={`${r.name} entfernen`} title="Entfernen">
                        ✕
                      </button>
                      <span {...sort.handle({ group: "list", index: i }, r.name || "Tag")}>
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                          <path d={GRIP_PATH} stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                        </svg>
                      </span>
                    </>
                  )}
                  {/* Its translation right here (S61 § 4.1), once saved under this name. */}
                  {r.from && r.name === r.from && <PhraseLine text={r.from} data={data} />}
                  {picking === r.key && (
                    <div className="symbol-grid" role="radiogroup" aria-label={`Symbol für ${r.name}`} data-testid="symbol-grid">
                      <button className={!r.icon ? "active" : ""} onClick={() => [setPicking(null), void save(rows.map((x) => (x.key === r.key ? { ...x, icon: "" } : x)))]} role="radio" aria-checked={!r.icon}>
                        ohne
                      </button>
                      {TAG_ICONS.map((ic) => (
                        <button key={ic.id} className={r.icon === ic.id ? "active" : ""} onClick={() => [setPicking(null), void save(rows.map((x) => (x.key === r.key ? { ...x, icon: ic.id } : x)))]} title={ic.label} aria-label={ic.label} role="radio" aria-checked={r.icon === ic.id} data-testid={`symbol-${ic.id}`}>
                          <TagSymbol id={ic.id} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </li>
          {fresh.length > 0 && (
            <li style={{ border: "none" }}>
              {fresh.map((r) => (
                <div key={r.key} className="category-row tag-row unsaved" data-testid="tag-new">
                  <span />
                  <input value={r.name} onChange={(e) => [setFresh(fresh.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x))), setState({})]} onBlur={() => void save(rows)} aria-label="Name des Tags" placeholder={drink ? "z. B. vom Fass" : "z. B. hausgemacht"} autoFocus />
                  <span className="count" />
                  <button className="link danger" onClick={() => setFresh(fresh.filter((x) => x.key !== r.key))} aria-label="Neuen Tag entfernen" title="Entfernen">
                    ✕
                  </button>
                  {/* Where the grip will be once saved: the columns stay. */}
                  <span className="grip" style={{ visibility: "hidden" }} aria-hidden="true" />
                </div>
              ))}
            </li>
          )}
        </ul>
        {fresh.length > 0 && (
          <p className="warn" data-testid="tags-unsaved">
            Noch nicht gespeichert: Ein neuer Tag wird gespeichert, sobald er einen Namen hat.
          </p>
        )}
        {canEdit && (
          <div className="buttons">
            <button onClick={() => [setFresh([...fresh, { key: nextKey++, name: "", icon: "" }]), setState({})]} data-testid="add-tag">
              + Tag
            </button>
          </div>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && <p className="ok-text">{state.ok}</p>}
        <p className="hint">{drink ? "Jede Änderung wird gespeichert, sobald du das Feld verlässt oder ein Symbol wählst. Umbenennen ändert den Tag bei allen Getränken und Speisekarten. Entfernen nimmt ihn den Getränken weg (Zahl rechts: so viele haben ihn)." : "Jede Änderung wird gespeichert, sobald du das Feld verlässt oder ein Symbol wählst. Umbenennen ändert den Tag bei allen Speisen und Speisekarten. Entfernen nimmt ihn den Speisen weg (Zahl rechts: so viele haben ihn)."}</p>
        <p className="hint">◇ links: ein Symbol für den Tag. Auf der Speisekarte steht dann das Symbol statt des Worts, unten unter „Symbole“ erklärt.</p>
      </section>
    </div>
  );
}
